import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CreateRequestModal = ({ isOpen, onClose, onSubmit, documents }) => {
  const [formData, setFormData] = useState({
    documentId: '',
    title: '',
    justification: '',
    originalContent: '',
    proposedContent: '',
    priority: 'medium',
    attachments: []
  });
  const [dragActive, setDragActive] = useState(false);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (files) => {
    const newAttachments = Array.from(files)?.map(file => ({
      name: file?.name,
      size: `${(file?.size / 1024)?.toFixed(1)} KB`,
      type: file?.type,
      file: file
    }));
    
    setFormData(prev => ({
      ...prev,
      attachments: [...prev?.attachments, ...newAttachments]
    }));
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileUpload(e?.dataTransfer?.files);
    }
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev?.attachments?.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    const selectedDoc = documents?.find(doc => doc?.id === formData?.documentId);
    
    const requestData = {
      ...formData,
      documentName: selectedDoc?.name || 'Unknown Document',
      submissionDate: new Date()?.toISOString(),
      status: 'pending',
      requester: 'Current User' // In real app, get from auth context
    };
    
    onSubmit(requestData);
    
    // Reset form
    setFormData({
      documentId: '',
      title: '',
      justification: '',
      originalContent: '',
      proposedContent: '',
      priority: 'medium',
      attachments: []
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-500 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Create Change Request</h2>
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              iconSize={18}
              onClick={onClose}
            />
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Document Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Select Document *
              </label>
              <select
                value={formData?.documentId}
                onChange={(e) => handleInputChange('documentId', e?.target?.value)}
                required
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Choose a document...</option>
                {documents?.map(doc => (
                  <option key={doc?.id} value={doc?.id}>{doc?.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Priority
              </label>
              <select
                value={formData?.priority}
                onChange={(e) => handleInputChange('priority', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          {/* Title */}
          <Input
            label="Request Title *"
            type="text"
            value={formData?.title}
            onChange={(e) => handleInputChange('title', e?.target?.value)}
            placeholder="Brief description of the change"
            required
          />

          {/* Justification */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Justification *
            </label>
            <textarea
              value={formData?.justification}
              onChange={(e) => handleInputChange('justification', e?.target?.value)}
              placeholder="Explain why this change is needed..."
              required
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows={4}
            />
          </div>

          {/* Content Changes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Original Content
              </label>
              <textarea
                value={formData?.originalContent}
                onChange={(e) => handleInputChange('originalContent', e?.target?.value)}
                placeholder="Current content that needs to be changed..."
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                rows={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Proposed Content *
              </label>
              <textarea
                value={formData?.proposedContent}
                onChange={(e) => handleInputChange('proposedContent', e?.target?.value)}
                placeholder="New content to replace the original..."
                required
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                rows={6}
              />
            </div>
          </div>

          {/* File Attachments */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Attachments
            </label>
            
            {/* Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive 
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-2">
                Drag and drop files here, or click to select
              </p>
              <input
                type="file"
                multiple
                onChange={(e) => handleFileUpload(e?.target?.files)}
                className="hidden"
                id="file-upload"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                Choose Files
              </Button>
            </div>

            {/* Attachment List */}
            {formData?.attachments?.length > 0 && (
              <div className="mt-3 space-y-2">
                {formData?.attachments?.map((attachment, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 border border-border rounded-lg">
                    <Icon name="Paperclip" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground flex-1">{attachment?.name}</span>
                    <span className="text-xs text-muted-foreground">{attachment?.size}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      iconSize={14}
                      onClick={() => removeAttachment(index)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <Button
              type="submit"
              iconName="Send"
              iconSize={16}
              disabled={!formData?.documentId || !formData?.title || !formData?.justification || !formData?.proposedContent}
            >
              Submit Request
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRequestModal;